/**
 * NotificationsContext — Central de Sugestões proativas.
 *
 * Faz polling a cada 3 minutos em `/tenant/notifications` e expõe:
 *   - items, total, byCategory, countsByPriority
 *   - resolve(id) / snooze(id, days) / dismiss(id) / refresh(force?)
 *
 * Só ativa quando há tenant autenticado.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 3 * 60 * 1000;

const Ctx = createContext(null);

export function NotificationsProvider({ children }) {
  const auth = useAuth();
  const [state, setState] = useState({
    items: [],
    total: 0,
    byCategory: {},
    countsByPriority: { high: 0, medium: 0, low: 0 },
    loaded: false,
  });
  const timerRef = useRef(null);

  const load = useCallback(async (force = false) => {
    if (!auth?.tenant?.id) return;
    try {
      if (force) {
        try { await api.post("/tenant/notifications/regenerate"); } catch { /* ignore */ }
      }
      const { data } = await api.get("/tenant/notifications");
      setState({
        items: data.items || [],
        total: data.total || 0,
        byCategory: data.by_category || {},
        countsByPriority: data.counts_by_priority || { high: 0, medium: 0, low: 0 },
        loaded: true,
      });
    } catch (e) {
      setState((s) => ({ ...s, loaded: true }));
    }
  }, [auth?.tenant?.id]);

  useEffect(() => {
    if (!auth?.tenant?.id) return;
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [auth?.tenant?.id, load]);

  const resolve = useCallback(async (id) => {
    await api.post(`/tenant/notifications/${id}/resolve`);
    setState((s) => ({ ...s, items: s.items.filter((it) => it.id !== id), total: Math.max(0, s.total - 1) }));
  }, []);

  const snooze = useCallback(async (id, days = 7) => {
    await api.post(`/tenant/notifications/${id}/snooze`, { days });
    setState((s) => ({ ...s, items: s.items.filter((it) => it.id !== id), total: Math.max(0, s.total - 1) }));
  }, []);

  const dismiss = useCallback(async (id) => {
    await api.post(`/tenant/notifications/${id}/dismiss`);
    setState((s) => ({ ...s, items: s.items.filter((it) => it.id !== id), total: Math.max(0, s.total - 1) }));
  }, []);

  return (
    <Ctx.Provider value={{ ...state, refresh: load, resolve, snooze, dismiss }}>
      {children}
    </Ctx.Provider>
  );
}

export const useNotifications = () => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return {
      items: [], total: 0, byCategory: {}, countsByPriority: { high: 0, medium: 0, low: 0 },
      loaded: false,
      refresh: async () => {}, resolve: async () => {}, snooze: async () => {}, dismiss: async () => {},
    };
  }
  return ctx;
};
